import { useGlobalStore } from "@/store/hooks";
import { Form, Radio, Select } from "antd";
import React, { useEffect, useImperativeHandle, useState } from "react";
import ItemQuantityRangeField from "../ItemQuantityRangeField";
import LogisticsCompanyField from "../LogisticsCompanyField";
import LogisticsInfoStatusField from "../LogisticsInfoStatusField";
import OrderMarkField from "../OrderMarkField";
import PaymentRangeField from "../PaymentRangeField";
import PrintStatusField from "../PrintStatusField";
import ShopQuickField from "../QuickField";
import SellerFlagField from "../SellerFlagField";
import ShipperField from "../ShipperField";
import UndeliverableAreaSelect from "../UndeliverableAreaField";
import AddressField from "../AddressField";
import ProductNameField from "../ProductNameField";
import Input from "../BaseInput";
import { FormInstance } from "antd";
import ShippingAreaSelect from "../ShippingAreaSelect";
import WeightRangeField from "../WeightRangeField";
import SkuPropertiesField from "../SkuPropertiesField";
import OrderItemExternalIdField from "../OrderItemExternalIdField";
import OrderTagSelectField from "../OrderTagSelectField";
import QuickEnum from "@/scenes/OrderManagement/enum/QuickEnum";
import LogisticsCompany from "@/enum/LogisticsCompany";
import ProductOriginalId from "../ProductOriginalId";
import SkuOriginalIdField from "../SkuOriginalIdField";
import SellerRemarksField from "../SellerRemarksField";
import OrderTagUnSelectField from "../OrderTagUnSelectField";
import QuickField from "@/scenes/OrderManagementForAllShop/components/SearchPane/QuickField";
import TimeType from "@/scenes/OrderManagement/enum/TimeType";
import { Input as AntInput } from "antd";
import TimeTypeFormField from "./TimeTypeFormField";
import LogisticsStagnantHoursField from "../LogisticsStagnantHoursField";
import RemainingShippingHoursField from "../RemainingShippingHoursField";
import WaybillTemplateSelect2 from "@/components/WaybillTemplateSelect2";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import PopoverText from "@/components/PopoverText";
import { ShippingDeadlineShopTypes } from "@/utils/ShopUtils";
import OrderSearchForm from "../OrderSearchForm";

interface FieldFormProps {
    shopId?: number;
    onFieldsChange();
}

export interface FieldFormValues {
    name: string;

    printStatus?: string;
    logisticsInfoStatus?: string;
    logisticsStagnantHours?: number;
    remainingShippingHours?: number;
    quick?: QuickEnum;
    undeliverableArea?: string[];
    sellerFlags?: string[];
    orderItemTitle?: string;
    provinceCode?: string;
    cityCode?: string;
    logisticsCompany?: LogisticsCompany;
    markIds?: (number | string)[];
    minPayment?: string;
    maxPayment?: string;
    minWeight?: string;
    maxWeight?: string;
    shippingAreaId?: number | string;
    skuProperties?: string;

    templateId: number;
}

export interface FieldFormInstance extends FormInstance<FieldFormValues> {}

const FieldForm: React.ForwardRefRenderFunction<FieldFormInstance, FieldFormProps> = function (props, ref) {
    const { shopId, onFieldsChange } = props;
    const { shopMapping, areaStore } = useGlobalStore();
    const shop = shopMapping.get(shopId);
    const [form] = Form.useForm();
    const [templates, setTemplates] = useState<WaybillTemplateDTO[]>([]);

    useImperativeHandle(ref, () => form);

    useEffect(() => {
        areaStore.ensureFhyArea();
    }, []);

    return (
        <OrderSearchForm
            layout="inline"
            className="field-form"
            form={form}
            onFieldsChange={onFieldsChange}
            colon={false}
        >
            <Form.Item
                label={
                    <span className="search-field-label" style={{ width: 52 }}>
                        条件名称
                    </span>
                }
                name="name"
                rules={[{ required: true, message: "请输入条件名称" }]}
                required
            >
                <Input maxLength={12} />
            </Form.Item>

            <Form.Item
                label={
                    <span className="search-field-label" style={{ width: 52 }}>
                        搜索模式
                    </span>
                }
                name="anyMatch"
                initialValue={false}
                className="ml15"
            >
                <Radio.Group size="small">
                    <Radio.Button value={false}>全部条件匹配</Radio.Button>
                    <Radio.Button value={true}>任一条件匹配</Radio.Button>
                </Radio.Group>
            </Form.Item>

            <div className="w" />

            {shop ? (
                <Form.Item
                    label={
                        <PopoverText content="设置快递单模板后点击该常用搜索进行搜索后打印时会自动选中该模版">
                            <span className="search-field-label" style={{ width: 65 }}>
                                快递单模板
                            </span>
                        </PopoverText>
                    }
                    name="templateId"
                    normalize={(template) => template?.id}
                    getValueProps={(value) => ({ value: templates.find(({ id }) => id === value) })}
                >
                    <WaybillTemplateSelect2
                        popupMatchSelectWidth={false}
                        size="small"
                        style={{ width: "300px" }}
                        shopId={shop.id}
                        filterableShopType={shop.type}
                        hideRefresh
                        onFilteredTemplatedLoad={setTemplates}
                        allowClear
                        disableDefaultOnChange
                    />
                </Form.Item>
            ) : null}

            <div className="w" />

            <div className="flex flex-center">
                <i
                    style={{
                        display: "inline-block",
                        background: "#407cff",
                        width: "3px",
                        height: "18px",
                        marginRight: "8px",
                        marginTop: "1px",
                    }}
                />
                <span style={{ fontWeight: "bold", fontSize: "15px" }}>搜索选项</span>
            </div>

            <div className="w" />
            <TimeTypeFormField />
            <div className="w" />

            <PrintStatusField />
            <LogisticsInfoStatusField />
            <LogisticsStagnantHoursField />
            {!shopId || (shop && ShippingDeadlineShopTypes.includes(shop.type)) ? (
                <RemainingShippingHoursField />
            ) : null}
            {shopId ? <ShopQuickField shopId={shopId} tab={undefined} /> : <QuickField />}
            <UndeliverableAreaSelect />
            <ShippingAreaSelect />
            {shop ? <ShipperField shop={shop} /> : null}
            <SellerFlagField shop={shop} />
            <ProductNameField />
            <SkuPropertiesField />
            <OrderItemExternalIdField />
            <ProductOriginalId />
            <SkuOriginalIdField />
            <AddressField />
            <LogisticsCompanyField />
            <SellerRemarksField />
            <OrderTagSelectField shopType={shop?.type} />
            <OrderTagUnSelectField shopType={shop?.type} />
            <OrderMarkField />
            <ItemQuantityRangeField />
            <PaymentRangeField shop={shop} />
            <WeightRangeField />
        </OrderSearchForm>
    );
};

export default React.forwardRef(FieldForm);
